import { type FC } from "react";
import {
  DetailEditModal,
  type EditFieldConfig,
} from "../../components/DetailEditModal";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import api from "../../../../util/api";
import { UserWithCompanyMemberships } from "../../../../util/types";

const NAME_REGEX = /^.{2,}$/;
const NAME_ERROR = "Must be at least 2 characters.";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_ERROR = "Invalid email format.";

interface EditUserModalProps {
  data: UserWithCompanyMemberships;
  onSuccess: () => void | Promise<void>;
}

export const EditUserModal: FC<EditUserModalProps> = ({ data, onSuccess }) => {
  const { showNotification } = useNotification();

  const fields: EditFieldConfig[] = [
    {
      name: "firstName",
      label: "First name",
      type: "text",
      required: true,
      regex: NAME_REGEX,
      regexMessage: NAME_ERROR,
    },
    {
      name: "lastName",
      label: "Last name",
      type: "text",
      required: true,
      regex: NAME_REGEX,
      regexMessage: NAME_ERROR,
    },
    {
      name: "email",
      label: "Email",
      type: "text",
      required: true,
      regex: EMAIL_REGEX,
      regexMessage: EMAIL_ERROR,
    },
  ];

  return (
    <DetailEditModal
      title="Edit user"
      fields={fields}
      initialValues={{
        firstName: data.firstName ?? "",
        lastName: data.lastName ?? "",
        email: data.email ?? "",
      }}
      onSubmit={async (values) => {
        try {
          await api.updateUserData(data._id, {
            firstName: String(values.firstName).trim(),
            lastName: String(values.lastName).trim(),
            email: String(values.email).trim(),
          });
          await Promise.resolve(onSuccess());
          showNotification("User updated", NotificationTypes.SUCCESS);
        } catch (e: unknown) {
          showNotification(
            getApiErrorMessage(e, "Could not save user"),
            NotificationTypes.WARNING
          );
          throw e;
        }
      }}
    />
  );
};
